/**
 * useBriefChat — manages chat state + SSE for the Brief Studio.
 *
 * Decoupled from any one app's `api.ts` via the BriefApiClient interface.
 * Both the brief-share SPA and the admin BriefWorkspace consume this hook.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { BriefApiClient, MentionableItem } from '../types';
import type { ChatMention as ComposerChatMention } from '../chat/ChatComposer';

export type ResultStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface PatchCard {
  kind: 'patch';
  patch_id: string;
  section: string;
  summary: string;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  status: ResultStatus;
  error?: string;
}

export interface RunCard {
  kind: 'run';
  run_id: string;
  skill: string;
  summary: string;
  payload: Record<string, unknown>;
  handoff_share_url?: string | null;
  status: ResultStatus;
  error?: string;
}

export interface LookupCard {
  kind: 'lookup';
  lookup_id: string;
  kind_of: 'persona' | 'pdo' | 'brand_voice' | 'products';
  summary: string;
  payload: Record<string, unknown>;
}

export type ResultCard = PatchCard | RunCard | LookupCard;

/** Re-export the composer's ChatMention so consumers and the hook agree
 *  on a single canonical type, avoiding nominal-mismatch errors when
 *  passing `chat.sendMessage` directly to `BriefChatPanel.onSend`. */
export type ChatMention = ComposerChatMention;

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  target_sections?: string[];
  target_angles?: string[];
  mentions?: ChatMention[];
  cards: ResultCard[];
}

export type MentionItem = MentionableItem;

interface ChatState {
  sessionId: string | null;
  messages: ChatMessage[];
  activeSections: string[];
  activeAngles: string[];
  isStreaming: boolean;
  error: string | null;
  editsRemaining: number | null;
  generationsRemaining: number | null;
  mentionItems: MentionItem[];
}

interface UseBriefChatOptions {
  onBriefPatched?: (section: string, after: Record<string, unknown> | null) => void;
  initialEdits?: number | null;
  initialGenerations?: number | null;
  apiClient?: BriefApiClient;
  /** Base URL for all API calls. Leave empty to use Vite proxy (dev). Required when
   *  the app is served from a different origin than the API (e.g. Firebase emulator). */
  apiBase?: string;
}

export function useBriefChat(token: string, opts: UseBriefChatOptions = {}) {
  const [state, setState] = useState<ChatState>({
    sessionId: null,
    messages: [],
    activeSections: [],
    activeAngles: [],
    isStreaming: false,
    error: null,
    editsRemaining: opts.initialEdits ?? null,
    generationsRemaining: opts.initialGenerations ?? null,
    mentionItems: [],
  });

  const abortRef = useRef<AbortController | null>(null);
  const onBriefPatched = opts.onBriefPatched;
  const apiClient = opts.apiClient;
  // Stable ref so URL changes (rare) don't invalidate all callbacks.
  const apiBaseRef = useRef((opts.apiBase ?? '').replace(/\/$/, ''));
  useEffect(() => {
    apiBaseRef.current = (opts.apiBase ?? '').replace(/\/$/, '');
  }, [opts.apiBase]);

  const API = (t: string) => `${apiBaseRef.current}/api/v1/public/brief/${t}`;

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch(`${API(token)}/chat/session`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          sessionId: data.session_id,
          messages: (data.messages || []).map(_hydrateMessage),
          activeSections: data.active_sections || [],
          activeAngles: data.active_angles || [],
        }));
      })
      .catch((e) => {
        if (!cancelled) console.warn('Failed to load brief chat session:', e);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const loadMentionItems = useCallback(() => {
    if (!token || !apiClient) return;
    apiClient
      .mentionables(token)
      .then((data) => {
        const items: MentionItem[] = [
          ...(data.personas || []).map((p) => ({
            id: p.id,
            type: 'persona' as const,
            label: p.label,
          })),
          ...(data.attachments || []).map((a) => ({
            id: a.id,
            type: 'attachment' as const,
            label: a.label,
          })),
          ...(data.sections || []).map((s) => ({
            id: s.id,
            type: 'section' as const,
            label: s.label,
          })),
        ];
        setState((prev) => ({ ...prev, mentionItems: items }));
      })
      .catch(() => {});
  }, [token, apiClient]);

  useEffect(() => {
    loadMentionItems();
  }, [loadMentionItems]);

  const toggleTarget = useCallback(
    async (kind: 'section' | 'angle', id: string) => {
      const bucket = kind === 'section' ? 'activeSections' : 'activeAngles';
      const current = state[bucket];
      const present = current.includes(id);
      const next = present ? current.filter((x) => x !== id) : [...current, id];
      setState((prev) => ({ ...prev, [bucket]: next } as ChatState));

      try {
        await fetch(`${API(token)}/chat/toggle-target`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ kind, id, action: present ? 'remove' : 'add' }),
        });
      } catch {
        setState((prev) => ({ ...prev, [bucket]: current } as ChatState));
      }
    },
    [token, state],
  );

  const sendMessage = useCallback(
    async (content: string, mentions?: ChatMention[]) => {
      if (!content.trim() || state.isStreaming) return;

      const userMsg: ChatMessage = {
        id: `msg_${Date.now()}`,
        role: 'user',
        content,
        timestamp: new Date().toISOString(),
        target_sections: [...state.activeSections],
        target_angles: [...state.activeAngles],
        mentions,
        cards: [],
      };

      const assistantMsg: ChatMessage = {
        id: `msg_${Date.now()}_a`,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        cards: [],
      };

      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, userMsg, assistantMsg],
        isStreaming: true,
        error: null,
      }));

      const cardMap = new Map<string, ResultCard>();

      const flush = (overrides?: Partial<ChatMessage>) => {
        setState((prev) => {
          const msgs = [...prev.messages];
          const lastIdx = msgs.length - 1;
          if (lastIdx >= 0 && msgs[lastIdx].role === 'assistant') {
            msgs[lastIdx] = {
              ...msgs[lastIdx],
              cards: Array.from(cardMap.values()),
              ...(overrides || {}),
            };
          }
          return { ...prev, messages: msgs };
        });
      };

      try {
        abortRef.current = new AbortController();
        const response = await fetch(`${API(token)}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content,
            target_sections: userMsg.target_sections,
            target_angles: userMsg.target_angles,
            mentions: mentions ?? [],
          }),
          signal: abortRef.current.signal,
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(
            (err as { detail?: { message?: string } | string })?.detail
              ? typeof (err as { detail?: unknown }).detail === 'string'
                ? (err as { detail: string }).detail
                : JSON.stringify((err as { detail: unknown }).detail)
              : `HTTP ${response.status}`,
          );
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const events = buffer.split('\n\n');
          buffer = events.pop() || '';

          for (const raw of events) {
            if (!raw.trim()) continue;
            const lines = raw.split('\n');
            const evLine = lines.find((l) => l.startsWith('event: '));
            const dtLine = lines.find((l) => l.startsWith('data: '));
            if (!dtLine) continue;
            const eventType = evLine ? evLine.slice(7).trim() : 'data';
            try {
              const data = JSON.parse(dtLine.slice(6));
              _applyEvent(
                eventType,
                data,
                cardMap,
                assistantMsg,
                setState,
                onBriefPatched,
              );
              flush();
            } catch {
              /* ignore malformed */
            }
          }
        }
      } catch (e) {
        if ((e as Error).name !== 'AbortError') {
          setState((prev) => ({ ...prev, error: (e as Error).message }));
        }
      } finally {
        setState((prev) => ({ ...prev, isStreaming: false }));
        abortRef.current = null;
      }
    },
    [token, state.activeSections, state.activeAngles, state.isStreaming, onBriefPatched],
  );

  const cancelStream = useCallback(() => {
    abortRef.current?.abort();
    setState((prev) => ({ ...prev, isStreaming: false }));
  }, []);

  const appendSyntheticPatchCard = useCallback(
    (patch: {
      section: string;
      summary: string;
      before?: Record<string, unknown> | null;
      after?: Record<string, unknown> | null;
    }) => {
      setState((prev) => {
        const newCard: PatchCard = {
          kind: 'patch',
          patch_id: `quick_${Date.now()}`,
          section: patch.section,
          summary: patch.summary,
          before: patch.before ?? null,
          after: patch.after ?? null,
          status: 'completed',
        };
        const msgs = [...prev.messages];
        const lastIdx = msgs.length - 1;
        if (lastIdx >= 0 && msgs[lastIdx].role === 'assistant') {
          msgs[lastIdx] = {
            ...msgs[lastIdx],
            cards: [...msgs[lastIdx].cards, newCard],
          };
        } else {
          msgs.push({
            id: `msg_quick_${Date.now()}`,
            role: 'assistant',
            content: '',
            timestamp: new Date().toISOString(),
            cards: [newCard],
          });
        }
        return { ...prev, messages: msgs };
      });
    },
    [],
  );

  return {
    ...state,
    toggleTarget,
    sendMessage,
    cancelStream,
    refreshMentionItems: loadMentionItems,
    appendSyntheticPatchCard,
  };
}

export type UseBriefChatReturn = ReturnType<typeof useBriefChat>;

function _hydrateMessage(raw: Record<string, unknown>): ChatMessage {
  const cards: ResultCard[] = [];
  for (const p of (raw.patches as Array<Record<string, unknown>>) || []) {
    cards.push({
      kind: 'patch',
      patch_id: (p.patch_id as string) || '',
      section: (p.section as string) || '',
      summary: (p.summary as string) || '',
      before: (p.before as Record<string, unknown>) || null,
      after: (p.after as Record<string, unknown>) || null,
      status: ((p.status as string) || 'completed') as ResultStatus,
      error: p.error as string | undefined,
    });
  }
  for (const r of (raw.runs as Array<Record<string, unknown>>) || []) {
    cards.push({
      kind: 'run',
      run_id: (r.run_id as string) || '',
      skill: (r.skill as string) || '',
      summary: (r.summary as string) || '',
      payload: (r.payload as Record<string, unknown>) || {},
      handoff_share_url: (r.handoff_share_url as string) || null,
      status: ((r.status as string) || 'completed') as ResultStatus,
      error: r.error as string | undefined,
    });
  }
  for (const l of (raw.lookups as Array<Record<string, unknown>>) || []) {
    cards.push({
      kind: 'lookup',
      lookup_id: (l.lookup_id as string) || '',
      kind_of: ((l.kind as string) || 'persona') as LookupCard['kind_of'],
      summary: (l.summary as string) || '',
      payload: (l.payload as Record<string, unknown>) || {},
    });
  }
  return {
    id: (raw.id as string) || `msg_${Math.random()}`,
    role: (raw.role as ChatMessage['role']) || 'assistant',
    content: (raw.content as string) || '',
    timestamp: (raw.timestamp as string) || new Date().toISOString(),
    target_sections: (raw.target_sections as string[]) || [],
    target_angles: (raw.target_angles as string[]) || [],
    mentions: (raw.mentions as ChatMention[]) || [],
    cards,
  };
}

function _applyEvent(
  eventType: string,
  data: Record<string, unknown>,
  cards: Map<string, ResultCard>,
  assistantMsg: ChatMessage,
  setState: React.Dispatch<React.SetStateAction<ChatState>>,
  onBriefPatched?: (section: string, after: Record<string, unknown> | null) => void,
) {
  if (eventType === 'message') {
    assistantMsg.content = (data.content as string) || '';
    return;
  }
  if (eventType === 'done') {
    const edits = data.edits_remaining as number | undefined;
    const gens = data.generations_remaining as number | undefined;
    setState((prev) => ({
      ...prev,
      editsRemaining: edits ?? prev.editsRemaining,
      generationsRemaining: gens ?? prev.generationsRemaining,
    }));
    return;
  }

  if (eventType === 'patch_start') {
    const id = (data.patch_id as string) || '';
    cards.set(id, {
      kind: 'patch',
      patch_id: id,
      section: (data.section as string) || '',
      summary: (data.summary as string) || '',
      status: 'processing',
    });
  } else if (eventType === 'patch_complete') {
    const id = (data.patch_id as string) || '';
    const prev = cards.get(id) as PatchCard | undefined;
    const after = (data.after as Record<string, unknown>) || null;
    cards.set(id, {
      kind: 'patch',
      patch_id: id,
      section: (data.section as string) || prev?.section || '',
      summary: (data.summary as string) || prev?.summary || '',
      before: (data.before as Record<string, unknown>) || null,
      after,
      status: 'completed',
    } as PatchCard);
    if (onBriefPatched && (data.section as string)) {
      onBriefPatched(data.section as string, after);
    }
  } else if (eventType === 'patch_error') {
    const id = (data.patch_id as string) || '';
    const prev = cards.get(id);
    cards.set(id, {
      kind: 'patch',
      patch_id: id,
      section: (prev as PatchCard)?.section || (data.section as string) || '',
      summary: (prev as PatchCard)?.summary || '',
      status: 'failed',
      error: (data.error as string) || 'Unknown error',
    } as PatchCard);
  } else if (eventType === 'run_start') {
    const id = (data.run_id as string) || '';
    cards.set(id, {
      kind: 'run',
      run_id: id,
      skill: (data.skill as string) || '',
      summary: (data.summary as string) || '',
      payload: {},
      status: 'processing',
    });
  } else if (eventType === 'run_complete') {
    const id = (data.run_id as string) || '';
    cards.set(id, {
      kind: 'run',
      run_id: id,
      skill: (data.skill as string) || '',
      summary: (data.summary as string) || '',
      payload: (data.payload as Record<string, unknown>) || {},
      handoff_share_url: (data.handoff_share_url as string) || null,
      status: 'completed',
    });
  } else if (eventType === 'run_error') {
    const id = (data.run_id as string) || '';
    const prev = cards.get(id);
    cards.set(id, {
      kind: 'run',
      run_id: id,
      skill: (prev as RunCard)?.skill || (data.skill as string) || '',
      summary: (prev as RunCard)?.summary || '',
      payload: {},
      status: 'failed',
      error: (data.error as string) || 'Unknown error',
    });
  } else if (eventType === 'lookup') {
    const id = (data.lookup_id as string) || '';
    cards.set(id, {
      kind: 'lookup',
      lookup_id: id,
      kind_of: ((data.kind as string) || 'persona') as LookupCard['kind_of'],
      summary: (data.summary as string) || '',
      payload: (data.payload as Record<string, unknown>) || {},
    });
  }
}
